import { useRouter } from 'next/router'
import { memo, useEffect, useReducer, useState, VFC } from 'react'
import {
  GrayDownArrowIcon,
  LeftDoubleArrowIcon,
  RightArrowIcon,
  RightDoubleArrowIcon,
} from 'src/assets/svgs'
import { useMessageContext } from 'src/compositions/Messages/MessageContext'
import {
  SearchTagsManagement,
  TagLabelBase,
} from 'src/compositions/Messages/TagsManagement'
import { CheckboxDiv } from 'src/elements/Checkbox'
import { SimpleRadioButtonDiv } from 'src/elements/RadioButton'
import {
  CreationSourceType,
  MATCHING_STATUSES,
  MatchingStatusType,
  ROOM_STATUSES,
  RoomStatusType,
} from 'src/models/Room'
import {
  _contributionsidebarshadow,
  _message_submenu,
  _message_submenu_subtitle,
  _message_tag_label,
  _messagesbg,
  _profilegreen,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import {
  convertMessagePageQuqry,
  convertTagIds,
  toMessagePageQuery,
} from 'src/utils/routes'
import styled, { css } from 'styled-components'
import { TipsLink } from '../Link'

const MATCHING_STATUSES_WITH_LAEBEL: {
  [key in MatchingStatusType]: string
} = {
  all_room: '全て',
  is_messaging: 'メッセージ中',
  matching_request: 'マッチングリクエスト',
}

type SearchFilterItemType = {
  label: string
  key: keyof CreationSourceType
}
type NestSearchFilterType = {
  name: '検索経由' | 'つながり申請経由' | '掲示板投稿経由'
  items: SearchFilterItemType[]
}
const NEST_SEARCH_FILTERS: NestSearchFilterType[] = [
  {
    name: '検索経由',
    items: [
      { key: 'sent_request_from_search', label: '自分→相手' },
      { key: 'recieved_request_from_search', label: '相手→自分' },
    ],
  },
  {
    name: 'つながり申請経由',
    items: [
      { key: 'sent_request_from_request_box', label: '自分→相手' },
      { key: 'recieved_request_from_request_box', label: '相手→自分' },
    ],
  },
  {
    name: '掲示板投稿経由',
    items: [
      { key: 'sent_request_from_contribution', label: '相手の投稿に送信' },
      { key: 'recieved_request_from_contribution', label: '自分の投稿に受信' },
    ],
  },
]
const SEARCH_FILTERS: SearchFilterItemType[] = [
  { key: 'requested_from_other', label: 'その他' },
  { key: 'creation_source_is_referrer', label: '紹介経由' },
  { key: 'creation_source_is_event', label: 'イベント経由' },
]

const ROOM_STATUS_TYPE_WITH_LABEL: {
  [key in RoomStatusType]: string
} = {
  unspecified: '指定なし',
  unread_only: '未読のルーム',
  has_memo: 'メモ入力済みのルーム',
  hidden: '非表示',
  company_message_room: '2社間',
  company_message_group_room: '3社間',
}

const REFERRER_ROOM_STATUSES = ['normal', 'deleted'] as const
type ReferrerRoomStatusType = typeof REFERRER_ROOM_STATUSES[number]
const REFERRER_ROOM_STATUSES_WITH_LABEL: {
  [key in ReferrerRoomStatusType]: string
} = {
  normal: '通常ルーム',
  deleted: '削除ルーム',
}
const covertBoolToString = (bool?: boolean) => (bool ? 'true' : 'false')

const TAG_LABEL_LIMIT = 10 // 3点リーダーで切り取る長さです
const displayTagName = (tagName: string) => {
  return tagName.length > TAG_LABEL_LIMIT
    ? tagName.slice(0, TAG_LABEL_LIMIT) + '…'
    : tagName
}

export type SideMenuProps = {
  activeMatchingStatus: MatchingStatusType
  showTips?: boolean
  hasPaid: boolean
  activeCreationSource: CreationSourceType
  activeRoomStatus: RoomStatusType
  className?: string
  isReferrer: boolean
  showDeleted: boolean
  setShowDeleted: (bool: boolean) => void
}
export const MessageSideMenu: VFC<SideMenuProps> = memo(
  ({
    activeMatchingStatus,
    showTips,
    hasPaid,
    activeCreationSource,
    activeRoomStatus,
    className,
    isReferrer,
    showDeleted,
    setShowDeleted,
  }) => {
    const [collapsed, toggleCollapsed] = useReducer((prev) => !prev, false)
    const DEFAULT_EXTEND = false
    const [isFilterExtend, toggleIsFilterExtend] = useReducer(
      (prev) => !prev,
      DEFAULT_EXTEND,
    )
    const [isStatusExtend, toggleIsStatusExtend] = useReducer(
      (prev) => !prev,
      DEFAULT_EXTEND,
    )
    const [isTagExtend, toggleIsTagExtend] = useReducer(
      (prev) => !prev,
      DEFAULT_EXTEND,
    )
    const [displayTagsManagement, setDisplayTagsManagement] =
      useState<boolean>(false)

    const [matchingStatus, setMatchingStatus] =
      useState<MatchingStatusType>(activeMatchingStatus)
    const [creationSource, setCreationSource] =
      useState<CreationSourceType>(activeCreationSource)
    const [roomStatus, setRoomStatus] =
      useState<RoomStatusType>(activeRoomStatus)

    useEffect(() => {
      setMatchingStatus(activeMatchingStatus)
    }, [activeMatchingStatus])
    useEffect(() => {
      setCreationSource(activeCreationSource)
    }, [activeCreationSource])
    useEffect(() => {
      setRoomStatus(activeRoomStatus)
    }, [activeRoomStatus])

    const router = useRouter()
    const query = convertMessagePageQuqry(router.query)
    const updateMatchingStatusQuery = (value: MatchingStatusType) => {
      router.push(
        toMessagePageQuery({
          pathname: router.pathname,
          query: {
            ...query,
            matching_status: value,
          },
        }),
      )
    }
    const updateCreationSourceQuery = (value: CreationSourceType) => {
      router.push(
        toMessagePageQuery({
          pathname: router.pathname,
          query: {
            ...query,
            sent_request_from_search: covertBoolToString(
              value.sent_request_from_search,
            ),
            recieved_request_from_search: covertBoolToString(
              value.recieved_request_from_search,
            ),
            sent_request_from_request_box: covertBoolToString(
              value.sent_request_from_request_box,
            ),
            recieved_request_from_request_box: covertBoolToString(
              value.recieved_request_from_request_box,
            ),
            sent_request_from_contribution: covertBoolToString(
              value.sent_request_from_contribution,
            ),
            recieved_request_from_contribution: covertBoolToString(
              value.recieved_request_from_contribution,
            ),
            creation_source_is_referrer: covertBoolToString(
              value.creation_source_is_referrer,
            ),
            creation_source_is_event: covertBoolToString(
              value.creation_source_is_event,
            ),
            requested_from_other: covertBoolToString(
              value.requested_from_other,
            ),
          },
        }),
      )
    }
    const updateRoomStatusQuery = (value: RoomStatusType) => {
      router.push(
        toMessagePageQuery({
          pathname: router.pathname,
          query: {
            ...query,
            unread_only: value === 'unread_only' ? 'true' : null,
            has_memo: value === 'has_memo' ? 'true' : null,
            visibility_status: value === 'hidden' ? 'hidden' : null,
          },
        }),
      )
    }

    const tagIds = convertTagIds(query.tag_ids)
    const { tags } = useMessageContext()
    const searchTags = tags.filter((tag) => tagIds.includes(tag.id))

    return (
      <Layout className={className} collapsed={collapsed}>
        <ButtonWrapper>
          <CollapseButton onClick={toggleCollapsed}>
            {collapsed ? <RightDoubleArrowIcon /> : <LeftDoubleArrowIcon />}
          </CollapseButton>
        </ButtonWrapper>
        <Content>
          {isReferrer && (
            <>
              <TitleBox>紹介担当用</TitleBox>
              <SimpleRadioButtonDiv>
                {REFERRER_ROOM_STATUSES.map((value) => (
                  <RadioButtonDiv key={value}>
                    <input
                      name="referrerRoomStatus"
                      onChange={() =>
                        setShowDeleted(value === 'deleted' ? true : false)
                      }
                      id={value}
                      checked={value === 'deleted' ? showDeleted : !showDeleted}
                      type="radio"
                    />
                    <label htmlFor={value}>
                      {REFERRER_ROOM_STATUSES_WITH_LABEL[value]}
                    </label>
                  </RadioButtonDiv>
                ))}
              </SimpleRadioButtonDiv>
            </>
          )}
          {!showDeleted && (
            <>
              <TitleBox>ルームタイプ</TitleBox>
              <SimpleRadioButtonDiv>
                {MATCHING_STATUSES.map((value) => (
                  <RadioButtonDiv key={value}>
                    <input
                      name="matchingStatus"
                      onChange={({ target: { value: val } }) => {
                        updateMatchingStatusQuery(val as MatchingStatusType)
                      }}
                      id={value}
                      value={value}
                      checked={matchingStatus === value}
                      type="radio"
                    />
                    <label htmlFor={value}>
                      {MATCHING_STATUSES_WITH_LAEBEL[value]}
                    </label>
                  </RadioButtonDiv>
                ))}
              </SimpleRadioButtonDiv>

              <TitleBox>検索フィルタ</TitleBox>
              {/* NOTE: 有料会員のみ検索フィルタを表示 */}
              {hasPaid && (
                <>
                  <ChildTitleBoxWrapper>
                    <ChildTitleBox>
                      <ChildTitle>導線</ChildTitle>
                      <button onClick={toggleIsFilterExtend}>
                        {isFilterExtend ? (
                          <GrayDownArrowIcon />
                        ) : (
                          <RightArrowIcon />
                        )}
                      </button>
                    </ChildTitleBox>
                    <Container opened={isFilterExtend}>
                      {NEST_SEARCH_FILTERS.map((NestsearchFilter) => (
                        <div key={NestsearchFilter.name}>
                          <GrandchildTitle>
                            {NestsearchFilter.name}
                          </GrandchildTitle>
                          <RoomFileterBox>
                            <FilterCheckboxes
                              items={NestsearchFilter.items}
                              creationSource={creationSource}
                              updateCreationSourceQuery={
                                updateCreationSourceQuery
                              }
                            />
                          </RoomFileterBox>
                        </div>
                      ))}
                      <FilterCheckboxes
                        items={SEARCH_FILTERS}
                        creationSource={creationSource}
                        updateCreationSourceQuery={updateCreationSourceQuery}
                      />
                    </Container>
                  </ChildTitleBoxWrapper>
                </>
              )}
              <ChildTitleBoxWrapper>
                <ChildTitleBox>
                  <ChildTitle>ステータス</ChildTitle>
                  <button onClick={toggleIsStatusExtend}>
                    {isStatusExtend ? (
                      <GrayDownArrowIcon />
                    ) : (
                      <RightArrowIcon />
                    )}
                  </button>
                </ChildTitleBox>
                <Container opened={isStatusExtend}>
                  <SimpleRadioButtonDiv>
                    {ROOM_STATUSES.map((value) => {
                      if (isReferrer && value === 'hidden') return <></>
                      if (
                        value === 'company_message_room' ||
                        value === 'company_message_group_room'
                      )
                        return <></>
                      return (
                        <RadioButtonDiv key={value}>
                          <input
                            name="roomStatus"
                            onChange={({ target: { value: val } }) => {
                              updateRoomStatusQuery(val as RoomStatusType)
                            }}
                            id={value}
                            value={value}
                            checked={roomStatus === value}
                            type="radio"
                          />
                          <label htmlFor={value}>
                            {ROOM_STATUS_TYPE_WITH_LABEL[value]}
                          </label>
                        </RadioButtonDiv>
                      )
                    })}
                  </SimpleRadioButtonDiv>
                </Container>
              </ChildTitleBoxWrapper>

              {/* NOTE: 有料会員のみタグ管理を表示 */}
              {hasPaid && (
                <ChildTitleBoxWrapper>
                  <ChildTitleBox>
                    <ChildTitle>タグ</ChildTitle>
                    <button onClick={toggleIsTagExtend}>
                      {isTagExtend ? <GrayDownArrowIcon /> : <RightArrowIcon />}
                    </button>
                  </ChildTitleBox>
                  {isTagExtend && (
                    <InputTagDiv onClick={() => setDisplayTagsManagement(true)}>
                      <InputTags>
                        {searchTags.length > 0 ? (
                          searchTags.map((tag) => (
                            <TagLabel key={tag.id}>
                              {displayTagName(tag.name)}
                            </TagLabel>
                          ))
                        ) : (
                          <InputTagPlaceHolder>
                            ＋ 検索するタグを追加
                          </InputTagPlaceHolder>
                        )}
                      </InputTags>
                    </InputTagDiv>
                  )}
                  {displayTagsManagement && (
                    <SearchTagsManagement
                      searchTags={searchTags}
                      positions={{ top: 10, left: -16 }}
                      close={() => setDisplayTagsManagement(false)}
                    />
                  )}
                </ChildTitleBoxWrapper>
              )}
              {/* NOTE: 2022.08.24 一時的にコメントアウト */}
              {/* <SideMenuLink
                href={BOARD}
                Icon={BoardIcon}
                label="カンバン管理"
                isCurrent={selectedMode === 'BOARD'}
                enabled={true}
              /> */}
            </>
          )}
        </Content>
        {showTips && (
          <TipsLinkWrap>
            <TipsLink
              to="https://note.com/outbound_sales/n/n297eecbc350c"
              color="blue"
            />
          </TipsLinkWrap>
        )}
      </Layout>
    )
  },
)

const FilterCheckboxes: VFC<{
  items: SearchFilterItemType[]
  creationSource: CreationSourceType
  updateCreationSourceQuery: (value: CreationSourceType) => void
}> = ({ items, creationSource, updateCreationSourceQuery }) => (
  <>
    {items.map((filterItem) => (
      <CheckboxDiv key={filterItem.key} color={_profilegreen}>
        <input
          type="checkbox"
          id={filterItem.key}
          onChange={({ target: { checked } }) => {
            updateCreationSourceQuery({
              ...creationSource,
              [filterItem.key]: checked,
            })
          }}
          checked={creationSource[filterItem.key]}
        />
        <CheckboxLabel htmlFor={filterItem.key}>
          {filterItem.label}
        </CheckboxLabel>
      </CheckboxDiv>
    ))}
  </>
)

const Content = styled.div`
  ul {
    margin-left: 12px;
  }
`
const ButtonWrapper = styled.div`
  position: relative;
  height: 24px;
`
const CollapseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
`
const Layout = styled.aside<{ collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  width: 240px;
  overflow-y: scroll;
  padding: 20px 24px;
  background-color: ${_messagesbg};
  box-shadow: ${_contributionsidebarshadow};

  label {
    color: ${_message_submenu};
    white-space: nowrap;
  }

  transition: width 0.25s ease-in;
  ${Content} {
    transition: opacity 0.25s ease-in;
  }
  ${({ collapsed }) =>
    collapsed &&
    css`
      width: 56px;
      overflow-y: hidden;
      ${Content} {
        opacity: 0;
      }
    `}

  @media screen and (max-width: 960px) {
    display: none;
  }
`
const RadioButtonDiv = styled.div`
  padding-top: 12px;
`
const TitleBox = styled.div`
  color: ${_message_submenu};
  white-space: nowrap;
  padding-top: 22px;
  padding-bottom: 18px;
  font-weight: ${fontWeightBold};
`
const ChildTitle = styled.p`
  color: ${_message_submenu_subtitle};
  font-size: 12px;
  font-weight: ${fontWeightBold};
`
const GrandchildTitle = styled.p`
  color: ${_message_submenu_subtitle};
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 12px;
  font-weight: ${fontWeightBold};
`
const Container = styled.div<{ opened: boolean }>`
  margin-left: 8px;
  display: ${({ opened }) => (opened ? 'block' : 'none')};
`
const RoomFileterBox = styled.div`
  margin-left: 12px;
`
const CheckboxLabel = styled.label`
  margin-left: 4px;
  cursor: pointer;
`
const ChildTitleBoxWrapper = styled.div`
  margin-bottom: 23px;
`
const ChildTitleBox = styled.div`
  display: flex;
  justify-content: space-between;
`

const InputTagDiv = styled.div`
  margin-top: 12px;
  width: 192px;
  min-height: 84px;
  cursor: pointer;
  background: ${truewhite};
  border: 1px solid ${_message_tag_label};
  border-radius: 6px;
`
const InputTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 6px;
`
const TagLabel = styled.label`
  ${TagLabelBase};
  margin-right: 4px;
  margin-bottom: 4px;
`
const InputTagPlaceHolder = styled.a`
  color: ${_message_submenu_subtitle};
  width: 100%;
  text-align: center;
  line-height: 72px;
`

const TipsLinkWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: end;
`
